import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import { Button } from '../components/Button';
import { useToast } from '../components/Toast';
import { Input, Label, Select } from '../components/Input';
import { Save, Clock, Store, Percent, Share2, Calendar, Link as LinkIcon, X, Lock, Smartphone, Image, Upload, Sparkles, Trash2, RefreshCw, Mic } from 'lucide-react';
// MIGRATION FIREBASE: Utilise Firebase direct au lieu de l'API Python
import { settingsService, uploadService } from '../services/firebaseService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { isStandby } from '../featureStandby';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const DAYS = [
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' },
  { key: 'sunday', label: 'Dimanche' }
];

export const Settings = () => {
  const toast = useToast();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  // Build 259 BIS: Imprimante Bluetooth
  const [printerConnected, setPrinterConnected] = useState(false);
  const [connectingPrinter, setConnectingPrinter] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // FIREBASE DIRECT
      console.log('[SETTINGS] Chargement des paramètres...');
      const result = await settingsService.get();
      console.log('[SETTINGS] Résultat brut:', result);
      const data = result.settings || {};
      console.log('[SETTINGS] Données:', data);
      const finalSettings = {
        ...data,
        social_media: data.social_media || {},
        service_links: data.service_links || {},
        opening_hours: data.opening_hours || {},
        order_hours: data.order_hours || {},
        home_hero_image: data.home_hero_image || '',
        home_tagline: data.home_tagline || ''
      };
      console.log('[SETTINGS] Settings finaux:', finalSettings);
      setSettings(finalSettings);
    } catch (error) {
      console.error('[SETTINGS] ERREUR chargement:', error);
      setSettings({
        name: "Family's Bourg-en-Bresse",
        email: 'contact@familys.app',
        phone: '04 74 XX XX XX',
        address: '123 Avenue de la Gare, 01000 Bourg-en-Bresse',
        order_cutoff_minutes: 20,
        preparation_time_minutes: 15,
        enable_delivery: true,
        enable_takeaway: true,
        enable_onsite: true,
        loyalty_percentage: 5.0,
        social_media: {},
        service_links: {},
        opening_hours: {},
        order_hours: {},
        home_hero_image: '',
        home_tagline: 'Prêt à te régaler ?'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // BK-47: Valider que start < end pour chaque plage horaire active
    const hours = settings.order_hours || {};
    for (const [day, h] of Object.entries(hours)) {
      if (h.disabled) continue;
      if (h.start1 && h.end1 && h.start1 >= h.end1) {
        toast.error('Erreur horaires', `${day}: l'heure de début doit être avant l'heure de fin (plage 1)`);
        return;
      }
      if (h.start2 && h.end2 && h.start2 >= h.end2) {
        toast.error('Erreur horaires', `${day}: l'heure de début doit être avant l'heure de fin (plage 2)`);
        return;
      }
    }

    setSaving(true);
    try {
      // FIREBASE DIRECT
      await settingsService.update(settings);
      toast.success('Paramètres sauvegardés', 'Vos modifications ont été enregistrées');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erreur', 'Impossible d\'enregistrer les paramètres');
    } finally {
      setSaving(false);
    }
  };

  // Nettoyage des anciennes options produits
  const handleCleanupOptions = async () => {
    if (!window.confirm('Cette action va supprimer l\'ancien champ "options" de tous les produits qui ont déjà "option_ids".\n\nCela corrige l\'incohérence entre le backoffice et l\'app.\n\nContinuer ?')) {
      return;
    }

    setCleaningUp(true);
    setCleanupResult(null);

    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const cleanupFn = httpsCallable(functions, 'cleanupProductOptions');
      const result = await cleanupFn();

      setCleanupResult(result.data);

      if (result.data.cleaned_products > 0) {
        toast.success('Nettoyage terminé', `${result.data.cleaned_products} produit(s) nettoyé(s)`);
      } else {
        toast.success('Aucun nettoyage nécessaire', 'Tous les produits sont déjà propres');
      }
    } catch (error) {
      console.error('Error cleaning up options:', error);
      toast.error('Erreur', error.message || 'Impossible d\'exécuter le nettoyage');
    } finally {
      setCleaningUp(false);
    }
  };

  // Diagnostic et synchronisation des images options
  const [diagnosing, setDiagnosing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  const handleDiagnoseImages = async () => {
    setDiagnosing(true);
    setDiagResult(null);
    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const diagnoseFn = httpsCallable(functions, 'diagnoseOptionImages');
      const result = await diagnoseFn();
      setDiagResult(result.data);
      if (result.data.total_issues > 0) {
        toast.warning('Problèmes détectés', result.data.message);
      } else {
        toast.success('Tout est OK', 'Aucune incohérence d\'images détectée');
      }
    } catch (error) {
      console.error('Error diagnosing images:', error);
      toast.error('Erreur', error.message || 'Impossible d\'exécuter le diagnostic');
    } finally {
      setDiagnosing(false);
    }
  };

  const handleSyncImages = async () => {
    if (!window.confirm('Cette action va synchroniser toutes les images des options vers la bibliothèque.\n\nLes images dans choice_library seront mises à jour avec celles des options.\n\nContinuer ?')) {
      return;
    }
    setSyncing(true);
    setSyncResult(null);
    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const syncFn = httpsCallable(functions, 'syncOptionImagesToLibrary');
      const result = await syncFn();
      setSyncResult(result.data);
      toast.success('Synchronisation terminée', result.data.message);
    } catch (error) {
      console.error('Error syncing images:', error);
      toast.error('Erreur', error.message || 'Impossible d\'exécuter la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const updateSocialMedia = (platform, value) => {
    setSettings({
      ...settings,
      social_media: {
        ...settings.social_media,
        [platform]: value
      }
    });
  };

  const updateServiceLinks = (service, value) => {
    setSettings({
      ...settings,
      service_links: {
        ...settings.service_links,
        [service]: value
      }
    });
  };

  const updateHours = (day, type, field, value) => {
    const hoursField = type === 'opening' ? 'opening_hours' : 'order_hours';
    setSettings({
      ...settings,
      [hoursField]: {
        ...settings[hoursField],
        [day]: {
          ...settings[hoursField][day],
          [field]: value
        }
      }
    });
  };

  if (loading) {
    return (
      <div>
        <Header title="⚙️ Paramètres" subtitle="Configuration du restaurant" />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header title="⚙️ Paramètres" subtitle="Configuration du restaurant" />
      
      <div className="p-6 space-y-6">
        {/* Informations du restaurant */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
            <CardTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              Informations du restaurant
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Nom du restaurant *</Label>
                <Input
                  id="name"
                  value={settings.name || ''}
                  onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                  placeholder="Ex: Family's Bourg-en-Bresse"
                />
              </div>
              <div>
                <Label htmlFor="phone">Téléphone *</Label>
                <Input
                  id="phone"
                  value={settings.phone || ''}
                  onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  placeholder="Ex: 04 74 XX XX XX"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={settings.email || ''}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  placeholder="Ex: contact@familys.app"
                />
              </div>
              <div>
                <Label htmlFor="address">Adresse *</Label>
                <Input
                  id="address"
                  value={settings.address || ''}
                  onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                  placeholder="Ex: 123 Avenue de la Gare, 01000"
                />
              </div>
            </div>

            {/* TVA — taux applique au Ticket Z (cloture de journee).
                Le calcul lui-meme vit dans functions/vat.js (serveur) et son
                miroir admin-app/services/vat.ts : total_sales est un montant
                TTC, la TVA en est EXTRAITE (TTC x taux / (100 + taux)).
                Le choix DU TAUX est une question fiscale : elle appartient au
                restaurateur et a son comptable, pas au logiciel. */}
            <div className="border-t pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tva_rate">Taux de TVA du Ticket Z (%)</Label>
                  <Select
                    id="tva_rate"
                    value={settings.tva_rate !== undefined && settings.tva_rate !== null ? String(settings.tva_rate) : ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      tva_rate: e.target.value === '' ? null : parseFloat(e.target.value)
                    })}
                  >
                    <option value="">Non configuré (taux par défaut du logiciel)</option>
                    <option value="5.5">5,5 %</option>
                    <option value="10">10 %</option>
                    <option value="20">20 %</option>
                  </Select>
                </div>
                <div className="flex items-end">
                  <p className="text-xs text-gray-500 pb-2">
                    Utilisé pour la ligne « TVA collectée » de la clôture de
                    journée. À confirmer avec votre comptable : le taux dépend
                    de ce que vous vendez et du mode de consommation.
                  </p>
                </div>
              </div>
              {(settings.tva_rate === undefined || settings.tva_rate === null) && (
                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                  ⚠️ Aucun taux configuré : le Ticket Z applique le taux par
                  défaut du logiciel et l'indique sur le ticket. Choisissez le
                  taux ci-dessus une fois validé par votre comptable.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Page d'accueil de l'app */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              📱 Page d'accueil de l'application
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Image Hero */}
            <div>
              <Label className="mb-2 block">🖼️ Image de fond (Header)</Label>
              <p className="text-xs text-gray-500 mb-3">
                Cette image s'affiche en haut de la page d'accueil avec un dégradé. Format recommandé : <strong>1200x600px</strong>, JPG (max 300Ko).
              </p>
              
              <div className="flex gap-4 items-start">
                {/* Preview de l'image */}
                <div className="w-48 h-32 rounded-xl overflow-hidden border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center relative">
                  {settings.home_hero_image ? (
                    <>
                      <img 
                        src={settings.home_hero_image.startsWith('/') ? `${API_URL}${settings.home_hero_image}` : settings.home_hero_image}
                        alt="Hero"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, home_hero_image: '' })}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <div className="text-center text-gray-400">
                      <Image className="w-8 h-8 mx-auto mb-1" />
                      <span className="text-xs">Aucune image</span>
                    </div>
                  )}
                </div>
                
                {/* Upload */}
                <div className="flex-1">
                  <div className="flex gap-2 mb-2">
                    <label className="flex-1 cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            try {
                              // FIREBASE DIRECT
                              const result = await uploadService.uploadImage(file, 'settings');
                              setSettings({ ...settings, home_hero_image: result.url });
                            } catch (err) {
                              toast.error('Erreur', 'Impossible d\'uploader l\'image');
                            }
                          }
                        }}
                      />
                      <div className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors">
                        <Upload className="w-4 h-4" />
                        <span className="text-sm font-medium">Uploader une image</span>
                      </div>
                    </label>
                  </div>
                  <p className="text-xs text-gray-400">ou coller une URL :</p>
                  <Input
                    value={settings.home_hero_image || ''}
                    onChange={(e) => setSettings({ ...settings, home_hero_image: e.target.value })}
                    placeholder="https://exemple.com/image.jpg"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Phrase d'accroche */}
            <div>
              <Label htmlFor="home_tagline">✨ Phrase d'accroche</Label>
              <p className="text-xs text-gray-500 mb-2">
                Cette phrase s'affiche sous le message de salutation "Bonjour [Prénom] !"
              </p>
              <Input
                id="home_tagline"
                value={settings.home_tagline || ''}
                onChange={(e) => setSettings({ ...settings, home_tagline: e.target.value })}
                placeholder="Ex: Prêt à te régaler ? 🍔"
                maxLength={100}
              />
              <p className="text-xs text-gray-400 mt-1 text-right">
                {(settings.home_tagline || '').length}/100 caractères
              </p>
            </div>

            {/* Preview */}
            <div className="border rounded-xl overflow-hidden bg-gray-900">
              <div className="relative h-40">
                {settings.home_hero_image ? (
                  <img 
                    src={settings.home_hero_image.startsWith('/') ? `${API_URL}${settings.home_hero_image}` : settings.home_hero_image}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-amber-400 to-red-500 flex items-center justify-center">
                    <span className="text-6xl">🍔</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-amber-50" />
                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <p className="text-xl font-bold text-white drop-shadow-lg">Bonjour John ! ☀️</p>
                  <p className="text-sm text-gray-800 mt-1 bg-white/70 rounded-full px-3 py-1 inline-block">
                    {settings.home_tagline || 'Prêt à te régaler ?'}
                  </p>
                </div>
              </div>
              <div className="bg-amber-50 px-4 py-2 text-center">
                <span className="text-xs text-gray-500">👆 Aperçu de la page d'accueil</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Assistant IA */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              🤖 Assistant IA
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="openai_api_key">🔑 Clé API OpenAI</Label>
              <p className="text-xs text-gray-500 mb-2">
                Nécessaire pour utiliser l'assistant IA (analyse des ventes, suggestions promos, chat).
                Obtenez votre clé sur <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">platform.openai.com</a>
              </p>
              <div className="relative">
                <Input
                  id="openai_api_key"
                  type="password"
                  value={settings.openai_api_key || ''}
                  onChange={(e) => setSettings({ ...settings, openai_api_key: e.target.value })}
                  placeholder="sk-..."
                  className="font-mono"
                />
              </div>
              {settings.openai_api_key && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  ✅ Clé configurée
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="removebg_api_key">🖼️ Clé API Remove.bg</Label>
              <p className="text-xs text-gray-500 mb-2">
                Nécessaire pour supprimer les fonds d'images des produits.
                Obtenez votre clé sur <a href="https://www.remove.bg/api" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">remove.bg/api</a>
              </p>
              <div className="relative">
                <Input
                  id="removebg_api_key"
                  type="password"
                  value={settings.removebg_api_key || ''}
                  onChange={(e) => setSettings({ ...settings, removebg_api_key: e.target.value })}
                  placeholder="Votre clé API remove.bg..."
                  className="font-mono"
                />
              </div>
              {settings.removebg_api_key && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  ✅ Clé configurée
                </p>
              )}
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <p className="text-sm font-medium text-indigo-800 mb-2">💡 Fonctionnalités IA disponibles :</p>
              <ul className="text-xs text-indigo-700 space-y-1">
                <li>• <strong>Chat IA</strong> - Posez des questions sur votre activité</li>
                <li>• <strong>Analyse des ventes</strong> - Insights et recommandations automatiques</li>
                <li>• <strong>Suggestions promos</strong> - L'IA propose des promotions optimales</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Horaires */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Horaires du restaurant
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg mb-3">🏪 Horaires d'ouverture (affichés dans l'app)</h3>
              <p className="text-xs text-gray-500 mb-3">Vous pouvez définir 2 plages horaires par jour (ex: 11h-14h et 18h-23h)</p>
              <div className="space-y-3">
                {DAYS.map(day => (
                  <div key={day.key} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <label className="font-bold text-sm">{day.label}</label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={settings.opening_hours[day.key]?.closed || false}
                          onChange={(e) => {
                            const newHours = {...settings.opening_hours};
                            newHours[day.key] = { ...newHours[day.key], closed: e.target.checked };
                            setSettings({...settings, opening_hours: newHours});
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-red-600 font-medium">Fermé</span>
                      </label>
                    </div>
                    {!settings.opening_hours[day.key]?.closed && (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="time"
                            placeholder="Ouverture matin"
                            value={settings.opening_hours[day.key]?.open1 || ''}
                            onChange={(e) => {
                              const newHours = {...settings.opening_hours};
                              newHours[day.key] = { ...newHours[day.key], open1: e.target.value };
                              setSettings({...settings, opening_hours: newHours});
                            }}
                          />
                          <Input
                            type="time"
                            placeholder="Fermeture matin"
                            value={settings.opening_hours[day.key]?.close1 || ''}
                            onChange={(e) => {
                              const newHours = {...settings.opening_hours};
                              newHours[day.key] = { ...newHours[day.key], close1: e.target.value };
                              setSettings({...settings, opening_hours: newHours});
                            }}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="time"
                            placeholder="Ouverture soir (optionnel)"
                            value={settings.opening_hours[day.key]?.open2 || ''}
                            onChange={(e) => {
                              const newHours = {...settings.opening_hours};
                              newHours[day.key] = { ...newHours[day.key], open2: e.target.value };
                              setSettings({...settings, opening_hours: newHours});
                            }}
                          />
                          <Input
                            type="time"
                            placeholder="Fermeture soir (optionnel)"
                            value={settings.opening_hours[day.key]?.close2 || ''}
                            onChange={(e) => {
                              const newHours = {...settings.opening_hours};
                              newHours[day.key] = { ...newHours[day.key], close2: e.target.value };
                              setSettings({...settings, opening_hours: newHours});
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="font-semibold text-lg mb-3">📱 Horaires de commande (différents si nécessaire)</h3>
              <p className="text-sm text-gray-600 mb-3">Les clients ne pourront commander que pendant ces horaires (2 plages possibles)</p>
              <div className="space-y-3">
                {DAYS.map(day => (
                  <div key={day.key} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <label className="font-bold text-sm">{day.label}</label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={settings.order_hours[day.key]?.disabled || false}
                          onChange={(e) => {
                            const newHours = {...settings.order_hours};
                            newHours[day.key] = { ...newHours[day.key], disabled: e.target.checked };
                            setSettings({...settings, order_hours: newHours});
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-red-600 font-medium">Désactivé</span>
                      </label>
                    </div>
                    {!settings.order_hours[day.key]?.disabled && (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="time"
                            placeholder="Début 1"
                            value={settings.order_hours[day.key]?.start1 || ''}
                            onChange={(e) => {
                              const newHours = {...settings.order_hours};
                              newHours[day.key] = { ...newHours[day.key], start1: e.target.value };
                              setSettings({...settings, order_hours: newHours});
                            }}
                          />
                          <Input
                            type="time"
                            placeholder="Fin 1"
                            value={settings.order_hours[day.key]?.end1 || ''}
                            onChange={(e) => {
                              const newHours = {...settings.order_hours};
                              newHours[day.key] = { ...newHours[day.key], end1: e.target.value };
                              setSettings({...settings, order_hours: newHours});
                            }}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="time"
                            placeholder="Début 2 (optionnel)"
                            value={settings.order_hours[day.key]?.start2 || ''}
                            onChange={(e) => {
                              const newHours = {...settings.order_hours};
                              newHours[day.key] = { ...newHours[day.key], start2: e.target.value };
                              setSettings({...settings, order_hours: newHours});
                            }}
                          />
                          <Input
                            type="time"
                            placeholder="Fin 2 (optionnel)"
                            value={settings.order_hours[day.key]?.end2 || ''}
                            onChange={(e) => {
                              const newHours = {...settings.order_hours};
                              newHours[day.key] = { ...newHours[day.key], end2: e.target.value };
                              setSettings({...settings, order_hours: newHours});
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Réseaux sociaux */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
            <CardTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Réseaux sociaux
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">Ces liens apparaîtront sous forme d'icônes cliquables dans l'application mobile</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="facebook">🔵 Facebook</Label>
                <Input
                  id="facebook"
                  value={settings.social_media?.facebook || ''}
                  onChange={(e) => updateSocialMedia('facebook', e.target.value)}
                  placeholder="https://facebook.com/familysbourg"
                />
              </div>
              <div>
                <Label htmlFor="instagram">📷 Instagram</Label>
                <Input
                  id="instagram"
                  value={settings.social_media?.instagram || ''}
                  onChange={(e) => updateSocialMedia('instagram', e.target.value)}
                  placeholder="https://instagram.com/familysbourg"
                />
              </div>
              <div>
                <Label htmlFor="twitter">🐦 Twitter / X</Label>
                <Input
                  id="twitter"
                  value={settings.social_media?.twitter || ''}
                  onChange={(e) => updateSocialMedia('twitter', e.target.value)}
                  placeholder="https://twitter.com/familysbourg"
                />
              </div>
              <div>
                <Label htmlFor="tiktok">🎵 TikTok</Label>
                <Input
                  id="tiktok"
                  value={settings.social_media?.tiktok || ''}
                  onChange={(e) => updateSocialMedia('tiktok', e.target.value)}
                  placeholder="https://tiktok.com/@familysbourg"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Liens services */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-green-50 to-teal-50">
            <CardTitle className="flex items-center gap-2">
              <LinkIcon className="w-5 h-5" />
              Liens des services externes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">Gérez les liens de vos services de paiement et autres intégrations</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="stripe">💳 Stripe</Label>
                <Input
                  id="stripe"
                  value={settings.service_links?.stripe || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.startsWith('sk_') || val.startsWith('rk_')) {
                      return; // BUG-5 fix: bloquer les clés secrètes
                    }
                    updateServiceLinks('stripe', val);
                  }}
                  placeholder="https://dashboard.stripe.com/..."
                />
              </div>
              <div>
                <Label htmlFor="paypal">🅿️ PayPal</Label>
                <Input
                  id="paypal"
                  value={settings.service_links?.paypal || ''}
                  onChange={(e) => updateServiceLinks('paypal', e.target.value)}
                  placeholder="Dashboard PayPal URL"
                />
              </div>
              <div>
                <Label htmlFor="analytics">📊 Analytics</Label>
                <Input
                  id="analytics"
                  value={settings.service_links?.analytics || ''}
                  onChange={(e) => updateServiceLinks('analytics', e.target.value)}
                  placeholder="Google Analytics URL"
                />
              </div>
              <div>
                <Label htmlFor="delivery">🚚 Service de livraison</Label>
                <Input
                  id="delivery"
                  value={settings.service_links?.delivery || ''}
                  onChange={(e) => updateServiceLinks('delivery', e.target.value)}
                  placeholder="URL du service de livraison"
                />
              </div>
              <div>
                <Label htmlFor="apple_pay">🍎 Apple Pay</Label>
                <Input
                  id="apple_pay"
                  value={settings.service_links?.apple_pay || ''}
                  onChange={(e) => updateServiceLinks('apple_pay', e.target.value)}
                  placeholder="Lien Apple Pay"
                />
              </div>
              <div>
                <Label htmlFor="google_pay">🟢 Google Pay</Label>
                <Input
                  id="google_pay"
                  value={settings.service_links?.google_pay || ''}
                  onChange={(e) => updateServiceLinks('google_pay', e.target.value)}
                  placeholder="Lien Google Pay"
                />
              </div>
            </div>

            {/* Uber Eats */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                🥡 Livraison via Uber Eats
              </h4>
              <div>
                <Label htmlFor="uber_eats">Lien vers votre page Uber Eats</Label>
                <Input
                  id="uber_eats"
                  value={settings.service_links?.uber_eats || ''}
                  onChange={(e) => updateServiceLinks('uber_eats', e.target.value)}
                  placeholder="https://www.ubereats.com/fr/store/familys-bourg-en-bresse/..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ce lien sera affiché dans l'application mobile pour permettre aux clients de commander via Uber Eats
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Paramètres de commande */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-orange-50 to-red-50">
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Gestion des commandes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cutoff_time">⏱️ Délai minimum de commande</Label>
                <Select
                  id="cutoff_time"
                  value={settings.preparation_time_minutes || 15}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    setSettings({
                      ...settings,
                      preparation_time_minutes: value,
                      order_cutoff_minutes: value  // Sync les deux valeurs
                    });
                  }}
                >
                  <option value="10">10 minutes</option>
                  <option value="15">15 minutes</option>
                  <option value="20">20 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">1 heure</option>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  ⏰ Temps minimum avant qu'une commande puisse être récupérée. Les créneaux proposés aux clients tiendront compte de ce délai.
                </p>
              </div>

              <div>
                <Label htmlFor="time_slot_interval">🕐 Intervalle entre créneaux</Label>
                <Select
                  id="time_slot_interval"
                  value={settings.time_slot_interval || 15}
                  onChange={(e) => setSettings({ ...settings, time_slot_interval: parseInt(e.target.value) })}
                >
                  <option value="5">5 minutes</option>
                  <option value="10">10 minutes</option>
                  <option value="15">15 minutes</option>
                  <option value="20">20 minutes</option>
                  <option value="30">30 minutes</option>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  📅 Intervalle entre les créneaux de retrait proposés aux clients (ex: 12:00, 12:15, 12:30...)
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="minimum_order">💶 Commande minimum</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="minimum_order"
                    type="number"
                    min="0"
                    step="0.5"
                    value={settings.minimum_order || 0}
                    onChange={(e) => setSettings({ ...settings, minimum_order: parseFloat(e.target.value) || 0 })}
                    className="w-32"
                  />
                  <span className="text-gray-600">€</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  🛒 Montant minimum requis pour passer une commande (0 = pas de minimum)
                </p>
              </div>
            </div>

            <div>
              <Label>Types de commande activés</Label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={settings.enable_takeaway}
                    onChange={(e) => setSettings({ ...settings, enable_takeaway: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">🎒 À emporter</span>
                </label>
                <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={settings.enable_delivery}
                    onChange={(e) => setSettings({ ...settings, enable_delivery: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">🚚 Livraison</span>
                </label>
                <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={settings.enable_onsite}
                    onChange={(e) => setSettings({ ...settings, enable_onsite: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">🍽️ Sur place</span>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Programme de fidélité */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
            <CardTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5" />
              Programme de fidélité
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="loyalty">💰 Pourcentage de cashback</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="loyalty"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={settings.loyalty_percentage ?? 5}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    loyalty_percentage: parseFloat(e.target.value) 
                  })}
                  className="w-32"
                />
                <span className="text-gray-600">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                🎁 Pourcentage du montant de chaque commande crédité sur la carte de fidélité du client
              </p>
            </div>

            {/* Options cashback supplémentaires */}
            <div className="border-t pt-4 mt-4">
              <Label className="mb-3 block">Options de calcul du cashback</Label>
              
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.loyalty_exclude_promos_from_calculation || false}
                    onChange={(e) => setSettings({
                      ...settings,
                      loyalty_exclude_promos_from_calculation: e.target.checked
                    })}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium text-gray-900">Ne pas prendre en compte les promos dans le calcul</p>
                    <p className="text-xs text-gray-500">
                      Si activé, le cashback sera calculé sur le montant AVANT application des promotions
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.auto_badges_enabled || false}
                    onChange={(e) => setSettings({
                      ...settings,
                      auto_badges_enabled: e.target.checked
                    })}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium text-gray-900">🤖 Laisser l'IA gérer les badges produits</p>
                    <p className="text-xs text-gray-500">
                      L'IA décidera automatiquement quels produits afficher avec des badges (Best Seller, Nouveau, etc.)
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Codes PIN Modes */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              🔐 Codes PIN des Modes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Définissez des codes PIN à 4 chiffres pour sécuriser l'accès aux différents modes du back-office
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="pin_orders_mode">🍽️ Mode Commande</Label>
                <Input
                  id="pin_orders_mode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{4}"
                  maxLength="4"
                  value={settings.pin_orders_mode || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    if (value.length <= 4) {
                      setSettings({ ...settings, pin_orders_mode: value });
                    }
                  }}
                  placeholder="••••"
                  className="text-center text-2xl tracking-widest font-bold"
                />
                <p className="text-xs text-gray-500 mt-1">Accès : Dashboard, Pause, Rupture</p>
              </div>
              <div>
                <Label htmlFor="pin_delivery_mode">🚚 Mode Livraison</Label>
                <Input
                  id="pin_delivery_mode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{4}"
                  maxLength="4"
                  value={settings.pin_delivery_mode || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    if (value.length <= 4) {
                      setSettings({ ...settings, pin_delivery_mode: value });
                    }
                  }}
                  placeholder="••••"
                  className="text-center text-2xl tracking-widest font-bold"
                />
                <p className="text-xs text-gray-500 mt-1">Accès : Gestion des livraisons</p>
              </div>
              <div>
                <Label htmlFor="pin_admin_mode">🔑 Mode Admin</Label>
                <Input
                  id="pin_admin_mode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{4}"
                  maxLength="4"
                  value={settings.pin_admin_mode || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    if (value.length <= 4) {
                      setSettings({ ...settings, pin_admin_mode: value });
                    }
                  }}
                  placeholder="••••"
                  className="text-center text-2xl tracking-widest font-bold"
                />
                <p className="text-xs text-gray-500 mt-1">Accès : Back-office complet</p>
              </div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-4">
              <p className="text-sm text-yellow-800">
                ⚠️ Notez ces codes en lieu sûr. Le personnel aura besoin de ces codes pour accéder aux modes.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Maintenance & Outils */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-orange-50 to-red-50">
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              🔧 Maintenance & Outils
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Build 259: Mode Maintenance */}
            <div className="bg-white border-2 border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Lock className="w-4 h-4 text-red-500" />
                    Mode Maintenance
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Suspend temporairement les nouvelles commandes
                  </p>
                </div>
                <Button
                  onClick={async () => {
                    const newPaused = !settings?.is_paused;
                    try {
                      await settingsService.update({ is_paused: newPaused });
                      setSettings({ ...settings, is_paused: newPaused });
                      toast.success(newPaused ? 'Commandes suspendues' : 'Commandes réactivées');
                    } catch (error) {
                      console.error('Erreur toggle maintenance:', error);
                      toast.error('Erreur', 'Impossible de sauvegarder le paramètre');
                    }
                  }}
                  className={settings?.is_paused ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}
                >
                  {settings?.is_paused ? '▶️ Réactiver' : '⏸️ Suspendre'}
                </Button>
              </div>
              {settings?.is_paused && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  ⚠️ Les commandes sont actuellement suspendues
                </div>
              )}
            </div>

            {/* Commande Vocale IA */}
            <div className="bg-white border-2 border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Mic className="w-4 h-4 text-purple-500" />
                    Commande Vocale IA
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Active ou desactive le bouton de commande vocale IA sur l'app mobile
                  </p>
                  {/* Le standby (décision propriétaire) gagne sur ce réglage :
                      la fonction serveur refuse l'appel et le bouton micro est
                      masqué dans l'app, quelle que soit la valeur ci-dessous.
                      Afficher « activée » ici serait donc un mensonge. */}
                  {isStandby('voice_order') ? (
                    <p className="text-sm mt-2 font-medium text-gray-400">
                      ⏸️ Commande vocale en veille
                    </p>
                  ) : (
                    <p className={`text-sm mt-2 font-medium ${settings?.voice_order_enabled !== false ? 'text-green-600' : 'text-gray-400'}`}>
                      {settings?.voice_order_enabled !== false ? '✅ Commande vocale activée' : '⏸️ Commande vocale désactivée'}
                    </p>
                  )}
                </div>
                <Button
                  onClick={async () => {
                    const newEnabled = settings?.voice_order_enabled === false;
                    try {
                      await settingsService.update({ voice_order_enabled: newEnabled });
                      setSettings({ ...settings, voice_order_enabled: newEnabled });
                      toast.success(newEnabled ? 'Commande vocale activée' : 'Commande vocale désactivée');
                    } catch (error) {
                      console.error('Erreur toggle vocal:', error);
                      toast.error('Erreur', 'Impossible de sauvegarder le paramètre');
                    }
                  }}
                  disabled={isStandby('voice_order')}
                  className={settings?.voice_order_enabled !== false ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}
                >
                  {settings?.voice_order_enabled !== false ? '⏸️ Désactiver' : '▶️ Activer'}
                </Button>
              </div>
              {isStandby('voice_order') ? (
                <div className="mt-3 p-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700">
                  La commande vocale est en veille. Le bouton micro est masqué dans
                  l'app et les requêtes vocales sont refusées, quel que soit ce
                  réglage. Rien n'a été supprimé : elle peut être rallumée.
                </div>
              ) : settings?.voice_order_enabled === false && (
                <div className="mt-3 p-2 bg-purple-50 border border-purple-200 rounded text-sm text-purple-700">
                  Le bouton micro est masqué dans l'app et les requêtes vocales sont bloquées
                </div>
              )}
            </div>

            {/* Écrans de démarrage animés de l'app (rotation aléatoire) */}
            <div className="bg-white border-2 border-orange-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-orange-500" />
                Écrans de démarrage de l'app
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                À chaque ouverture, l'app tire au sort une animation parmi celles qui sont actives,
                jamais deux fois la même d'affilée. Le tout premier lancement joue toujours le Slam.
                Un changement s'applique à l'ouverture suivante de l'app.
              </p>
              <div className="mt-3 grid gap-2">
                {[
                  ['slam', 'Le Slam', 'Les ingrédients tombent, impact, le burger s\'ouvre sur l\'app'],
                  ['neon', "L'enseigne néon", 'Le logo grésille et s\'allume'],
                  ['window', 'La fenêtre burger', 'La photo actuelle s\'ouvre en forme de burger'],
                  ['cheddar', 'Le cheddar qui coule', 'Du cheddar fondu recouvre l\'écran'],
                  ['crumbs', "L'explosion de miettes", 'La photo éclate en miettes'],
                ].map(([id, label, desc]) => {
                  const active = settings?.splash_variants?.[id] !== false;
                  return (
                    <div key={id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-md px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{label}</p>
                        <p className="text-xs text-gray-500">{desc}</p>
                      </div>
                      <Button
                        onClick={async () => {
                          const current = { slam: true, neon: true, window: true, cheddar: true, crumbs: true, ...(settings?.splash_variants || {}) };
                          const next = { ...current, [id]: !active };
                          try {
                            await settingsService.update({ splash_variants: next });
                            setSettings({ ...settings, splash_variants: next });
                            toast.success(!active ? `${label} activé` : `${label} désactivé`);
                          } catch (error) {
                            console.error('Erreur toggle splash:', error);
                            toast.error('Erreur', 'Impossible de sauvegarder le paramètre');
                          }
                        }}
                        className={active ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-400 hover:bg-gray-500'}
                      >
                        {active ? 'Actif' : 'Inactif'}
                      </Button>
                    </div>
                  );
                })}
              </div>
              {['slam', 'neon', 'window', 'cheddar', 'crumbs'].every((id) => settings?.splash_variants?.[id] === false) && (
                <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded text-sm text-orange-700">
                  Aucune animation active : l'app jouera le Slam par défaut.
                </div>
              )}
            </div>

            {/* Build 259 BIS: Imprimante Bluetooth */}
            <div className="bg-white border-2 border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <span className="text-xl">🖨️</span>
                    Imprimante thermique Epson
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Connexion Bluetooth pour impression directe des tickets
                  </p>
                  <p className={`text-sm mt-2 font-medium ${printerConnected ? 'text-green-600' : 'text-gray-400'}`}>
                    {printerConnected ? '✅ Imprimante connectée' : '❌ Non connectée'}
                  </p>
                </div>
                <Button
                  onClick={async () => {
                    setConnectingPrinter(true);
                    try {
                      const thermalPrintService = (await import('../services/thermalPrintService')).default;
                      if (printerConnected) {
                        thermalPrintService.disconnect();
                        setPrinterConnected(false);
                        toast.success('Imprimante déconnectée');
                      } else {
                        await thermalPrintService.connect();
                        setPrinterConnected(true);
                        toast.success('Imprimante connectée !', `Appareil: ${thermalPrintService.device?.name || 'Inconnu'}`);
                      }
                    } catch (error) {
                      console.error('Erreur connexion imprimante:', error);
                      setPrinterConnected(false);
                      // Message d'erreur plus clair
                      const msg = error.message || 'Erreur inconnue';
                      if (msg.includes('annulee')) {
                        toast.warning('Connexion annulée', 'Vous avez fermé la fenêtre de sélection');
                      } else if (msg.includes('Bluetooth non disponible')) {
                        toast.error('Bluetooth non disponible', 'Utilisez Chrome ou Edge (pas Safari)');
                      } else {
                        toast.error('Erreur de connexion', msg);
                      }
                    } finally {
                      setConnectingPrinter(false);
                    }
                  }}
                  disabled={connectingPrinter}
                  className={printerConnected ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'}
                >
                  {connectingPrinter ? 'Recherche...' : printerConnected ? 'Déconnecter' : 'Connecter'}
                </Button>
              </div>
            </div>

            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Trash2 className="w-4 h-4 text-orange-500" />
                    Nettoyer les anciennes options produits
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Supprime l'ancien champ "options" des produits qui utilisent déjà "option_ids".
                    Corrige l'incohérence entre le backoffice et l'app.
                  </p>
                  {cleanupResult && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded text-sm">
                      <p className="font-medium text-green-800">
                        ✅ {cleanupResult.message}
                      </p>
                      {cleanupResult.cleaned_list && cleanupResult.cleaned_list.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-green-700 hover:underline">
                            Voir les produits nettoyés ({cleanupResult.cleaned_products})
                          </summary>
                          <ul className="mt-2 space-y-1 text-green-700">
                            {cleanupResult.cleaned_list.map(p => (
                              <li key={p.id}>• {p.name} ({p.oldOptionsCount} options supprimées)</li>
                            ))}
                          </ul>
                        </details>
                      )}
                      {cleanupResult.skipped_products > 0 && (
                        <p className="mt-2 text-yellow-700">
                          ⚠️ {cleanupResult.skipped_products} produit(s) utilisent encore l'ancien système
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleCleanupOptions}
                  disabled={cleaningUp}
                  className="ml-4 bg-orange-500 hover:bg-orange-600"
                >
                  {cleaningUp ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Nettoyage...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Nettoyer
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Diagnostic et Sync Images Options */}
            <div className="bg-white border rounded-lg p-4 mt-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Image className="w-4 h-4 text-blue-500" />
                    Diagnostic images options
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Vérifie si les images des choix dans les options correspondent à celles de la bibliothèque.
                    Utile pour diagnostiquer les incohérences d'images entre backoffice et app.
                  </p>
                  {diagResult && (
                    <div className={`mt-3 p-3 border rounded text-sm ${diagResult.total_issues > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
                      <p className={`font-medium ${diagResult.total_issues > 0 ? 'text-yellow-800' : 'text-green-800'}`}>
                        {diagResult.total_issues > 0 ? '⚠️' : '✅'} {diagResult.message}
                      </p>
                      <p className="text-gray-600 mt-1">
                        {diagResult.total_options} options, {diagResult.total_library} choix en bibliothèque
                      </p>
                      {diagResult.issues && diagResult.issues.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-yellow-700 hover:underline">
                            Voir les problèmes ({diagResult.total_issues})
                          </summary>
                          <ul className="mt-2 space-y-1 text-yellow-700 max-h-48 overflow-auto">
                            {diagResult.issues.map((issue, i) => (
                              <li key={i} className="text-xs">
                                • {issue.type === 'DIFFERENT' ? '❌' : '⚠️'} "{issue.choiceName}" dans "{issue.optionName}"
                                {issue.type === 'DIFFERENT' && <span className="text-gray-500"> (images différentes)</span>}
                                {issue.type === 'MISSING_IN_OPTION' && <span className="text-gray-500"> (pas d'image dans option)</span>}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}
                </div>
                <div className="ml-4 flex flex-col gap-2">
                  <Button
                    onClick={handleDiagnoseImages}
                    disabled={diagnosing}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    {diagnosing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Analyse...
                      </>
                    ) : (
                      <>
                        <Image className="w-4 h-4 mr-2" />
                        Diagnostiquer
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleSyncImages}
                    disabled={syncing || diagnosing}
                    className="bg-purple-500 hover:bg-purple-600"
                  >
                    {syncing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Sync...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Synchroniser
                      </>
                    )}
                  </Button>
                </div>
              </div>
              {syncResult && (
                <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded text-sm">
                  <p className="font-medium text-purple-800">
                    ✅ {syncResult.message}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bouton Enregistrer */}
        <div className="flex justify-end sticky bottom-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3 text-lg font-bold shadow-lg"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
          </Button>
        </div>
      </div>
    </div>
  );
};
// Force deploy dim. 14 déc. 2025 14:00:34 CET
// Deploy dim. 14 déc. 2025 16:52:55 CET
